"use client"

import { useAnalytics } from "components/Analytics/UnifiedAnalyticsProvider"
import { useCallback } from "react"

/**
 * Ticket information for tracking
 */
interface TicketInfo {
    id?: string
    name?: string
    title?: string
    price?: number
    quantity?: number
}

/**
 * Seat information for tracking
 */
interface SeatInfo {
    id?: string
    label?: string
    row?: string
    section?: string
}

/**
 * Payment result information
 */
interface PaymentResult {
    orderId: string
    value?: number
    currency?: string
    reason?: string
}

/**
 * Unified analytics tracking hook
 * Replaces useFacebookPixel with platform-agnostic tracking across all enabled analytics platforms
 *
 * This hook provides a consistent interface for tracking funnel events and automatically
 * dispatches them to all configured analytics platforms (Facebook Pixel, TikTok, Snapchat, GA4, etc.)
 */
export function useAnalyticsTracking() {
    const { trackEvent } = useAnalytics()

    /**
     * Track when a user views an event page
     */
    const sendEventViewed = useCallback(
        (eventId: string, eventTitle: string) => {
            trackEvent("event_viewed", {
                event_id: eventId,
                event_title: eventTitle,
                content_name: eventTitle,
                content_category: "Event",
                content_type: "event",
                content_id: eventId,
                content_ids: [eventId],
                currency: "SAR",
            })
        },
        [trackEvent]
    )

    /**
     * Track when a user selects a day/timeslot for an event
     */
    const sendDaySelected = useCallback(
        (eventId: string, eventTitle: string, day: string) => {
            trackEvent("day_selected", {
                event_id: eventId,
                event_title: eventTitle,
                content_name: eventTitle,
                content_category: "Event",
                day: day,
            })
        },
        [trackEvent]
    )

    /**
     * Track when a user selects a ticket type
     */
    const sendTicketSelected = useCallback(
        (
            eventId: string,
            eventTitle: string,
            ticket: string | TicketInfo,
            qty?: number
        ) => {
            const ticketName =
                typeof ticket === "string" ? ticket : ticket.name || ticket.title
            const quantity = typeof ticket === "object" ? ticket.quantity : qty

            trackEvent("ticket_selected", {
                event_id: eventId,
                event_title: eventTitle,
                content_name: eventTitle,
                content_category: "Event",
                content_type: "ticket",
                content_id: eventId,
                content_ids: [eventId],
                ticket: ticketName,
                quantity: quantity,
                value:
                    typeof ticket === "object" && ticket.price
                        ? ticket.price * (quantity || 1)
                        : undefined,
                currency: "SAR",
            })
        },
        [trackEvent]
    )

    /**
     * Track when a user selects a seat (for seated events)
     */
    const sendSeatSelected = useCallback(
        (eventId: string, ticket: string | TicketInfo, seat: string | SeatInfo) => {
            const ticketName =
                typeof ticket === "string" ? ticket : ticket.name || ticket.title
            const seatLabel =
                typeof seat === "string" ? seat : seat.label || `${seat.section}-${seat.row}`

            trackEvent("seat_selected", {
                event_id: eventId,
                content_name: eventId,
                content_category: "Event",
                content_type: "seat",
                ticket: ticketName,
                seat: seatLabel,
            })
        },
        [trackEvent]
    )

    /**
     * Track when a user fills out accreditation form
     */
    const sendAccreditationFilled = useCallback(
        (eventId: string) => {
            trackEvent("accreditation_filled", {
                event_id: eventId,
                content_name: eventId,
                content_category: "Event",
                content_type: "accreditation",
            })
        },
        [trackEvent]
    )

    /**
     * Track when a user applies a coupon/discount code
     */
    const sendCouponApplied = useCallback(
        (eventId: string, coupon: string, discount?: number) => {
            trackEvent("coupon_applied", {
                event_id: eventId,
                content_name: eventId,
                content_category: "Event",
                coupon: coupon,
                discount: discount,
            })
        },
        [trackEvent]
    )

    /**
     * Track when a user selects a payment method
     */
    const sendPaymentMethodSelected = useCallback(
        (eventId: string, paymentMethod: string) => {
            trackEvent("payment_method_selected", {
                event_id: eventId,
                content_name: eventId,
                content_category: "Event",
                payment_method: paymentMethod,
            })
        },
        [trackEvent]
    )

    /**
     * Track when a user proceeds to checkout
     */
    const sendProceedToCheckout = useCallback(
        (eventId: string, eventTitle?: string, totalValue?: number) => {
            trackEvent("proceed_to_checkout", {
                event_id: eventId,
                event_title: eventTitle,
                content_name: eventId,
                content_category: "Event",
                content_id: eventId,
                content_ids: [eventId],
                value: totalValue,
                currency: "SAR",
            })
        },
        [trackEvent]
    )

    // ===== NEW EXPANDED EVENTS =====

    /**
     * Track when a payment is successfully completed (purchase)
     * This is a conversion event that triggers server-side tracking
     */
    const sendPaymentCompleted = useCallback(
        (
            eventId: string,
            orderId: string,
            value?: number,
            currency?: string
        ) => {
            trackEvent(
                "purchase_completed",
                {
                    event_id: eventId,
                    order_id: orderId,
                    content_id: eventId,
                    content_ids: [eventId],
                    value: value,
                    currency: currency || "SAR",
                    content_type: "product",
                },
                "event",
                eventId
            )
        },
        [trackEvent]
    )

    /**
     * Track when a payment fails
     * This is a conversion event that triggers server-side tracking
     */
    const sendPaymentFailed = useCallback(
        (eventId: string, reason?: string) => {
            trackEvent(
                "payment_failed",
                {
                    event_id: eventId,
                    reason: reason,
                },
                "event",
                eventId
            )
        },
        [trackEvent]
    )

    /**
     * Track when a user shares an event
     */
    const sendShareEvent = useCallback(
        (eventId: string, platform: string) => {
            trackEvent("share_event", {
                event_id: eventId,
                share_platform: platform,
                method: platform,
                content_type: "event",
            })
        },
        [trackEvent]
    )

    /**
     * Track when a user adds an event to calendar
     */
    const sendAddToCalendar = useCallback(
        (eventId: string, eventTitle?: string) => {
            trackEvent("add_to_calendar", {
                event_id: eventId,
                event_title: eventTitle,
                content_name: eventTitle,
            })
        },
        [trackEvent]
    )

    /**
     * Track when a user performs a search
     */
    const sendSearchPerformed = useCallback(
        (query: string, resultsCount?: number) => {
            trackEvent("search_performed", {
                search_term: query,
                results_count: resultsCount,
            })
        },
        [trackEvent]
    )

    /**
     * Track when a user initiiates checkout (begin_checkout)
     * Standard funnel event for GA4 e-commerce
     */
    const sendBeginCheckout = useCallback(
        (
            eventId: string,
            eventTitle: string,
            items: Array<{ name: string; price: number; quantity: number }>,
            totalValue: number
        ) => {
            trackEvent("begin_checkout", {
                event_id: eventId,
                event_title: eventTitle,
                content_id: eventId,
                content_ids: [eventId],
                items: items,
                value: totalValue,
                currency: "SAR",
            })
        },
        [trackEvent]
    )

    /**
     * Track when tickets are added to cart (add_to_cart)
     * Standard funnel event for GA4 e-commerce
     */
    const sendAddToCart = useCallback(
        (
            eventId: string,
            eventTitle: string,
            ticket: { name: string; price: number },
            quantity: number
        ) => {
            trackEvent("add_to_cart", {
                event_id: eventId,
                event_title: eventTitle,
                content_id: eventId,
                content_ids: [eventId],
                items: [
                    {
                        item_name: ticket.name,
                        price: ticket.price,
                        quantity: quantity,
                    },
                ],
                value: ticket.price * quantity,
                currency: "SAR",
            })
        },
        [trackEvent]
    )

    /**
     * Track when a user views an item (view_item)
     * Standard funnel event for GA4 e-commerce
     */
    const sendViewItem = useCallback(
        (eventId: string, eventTitle: string, value?: number) => {
            trackEvent("view_item", {
                event_id: eventId,
                event_title: eventTitle,
                content_name: eventTitle,
                content_type: "event",
                content_id: eventId,
                content_ids: [eventId],
                value: value,
                currency: "SAR",
            })
        },
        [trackEvent]
    )

    /**
     * Track scroll depth milestone
     * Called by useEngagementTracking hook
     */
    const sendScrollDepth = useCallback(
        (eventId: string, depth: number) => {
            trackEvent("scroll_depth", {
                event_id: eventId,
                depth_percent: depth,
            })
        },
        [trackEvent]
    )

    /**
     * Track time on page milestone
     * Called by useEngagementTracking hook
     */
    const sendTimeOnPage = useCallback(
        (eventId: string, seconds: number) => {
            trackEvent("time_on_page", {
                event_id: eventId,
                time_seconds: seconds,
            })
        },
        [trackEvent]
    )

    /**
     * Legacy-compatible sendEvents object
     * Provides the same interface as useFacebookPixel for backward compatibility
     */
    const sendEvents = {
        sendEventViewed,
        sendDaySelected,
        sendTicketSelected,
        sendSeatSelected,
        sendAccreditationFilled,
        sendCouponApplied,
        sendPaymentMethodSelected,
        sendProceedToCheckout,
    }

    return {
        // Legacy interface (same as useFacebookPixel)
        sendEvents,

        // Individual methods (same as useFacebookPixel)
        sendEventViewed,
        sendDaySelected,
        sendTicketSelected,
        sendSeatSelected,
        sendAccreditationFilled,
        sendCouponApplied,
        sendPaymentMethodSelected,
        sendProceedToCheckout,

        // New expanded tracking methods
        sendPaymentCompleted,
        sendPaymentFailed,
        sendShareEvent,
        sendAddToCalendar,
        sendSearchPerformed,
        sendBeginCheckout,
        sendAddToCart,
        sendViewItem,
        sendScrollDepth,
        sendTimeOnPage,
    }
}

export default useAnalyticsTracking
